import React, { useState } from 'react';
import { MediaGalleryItem, LanguageCode } from '../types';
import { translations } from '../i18n/translations';
import { getLocalized, getLocalizedCategory } from '../utils/i18n';
import { Image as ImageIcon, Video, Play, X, Eye, ChevronLeft, ChevronRight, Share2, Download } from 'lucide-react';

interface GalleryViewProps {
  currentLang: LanguageCode;
  gallery: MediaGalleryItem[];
}

export const GalleryView: React.FC<GalleryViewProps> = ({ currentLang, gallery }) => {
  const t = translations[currentLang];
  const [filter, setFilter] = useState<'all' | 'photo' | 'video'>('all');
  const [activeMediaIndex, setActiveMediaIndex] = useState<number | null>(null);

  const filtered = gallery.filter((item) => {
    if (filter === 'all') return true;
    return item.type === filter;
  });

  const activeMedia = activeMediaIndex !== null ? filtered[activeMediaIndex] : null;

  const handlePrev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (activeMediaIndex === null) return;
    setActiveMediaIndex(activeMediaIndex > 0 ? activeMediaIndex - 1 : filtered.length - 1);
  };

  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (activeMediaIndex === null) return;
    setActiveMediaIndex(activeMediaIndex < filtered.length - 1 ? activeMediaIndex + 1 : 0);
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <span className="px-3.5 py-1 rounded-full bg-[#132238] text-[#38BDF8] text-xs font-extrabold uppercase tracking-wider border border-[#38BDF8]/40 shadow-[0_0_15px_rgba(56,189,248,0.2)]">
          {t.galleryBadge}
        </span>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white font-heading">
          {t.galleryMainTitle}
        </h1>
        <p className="text-sm text-gray-300">
          {t.gallerySubtitle}
        </p>
      </div>

      {/* Filter Buttons */}
      <div className="flex justify-center gap-3">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-2xl text-xs font-bold transition-all ${
            filter === 'all'
              ? 'bg-[#132238] text-[#38BDF8] border-2 border-[#38BDF8] shadow-[0_0_15px_rgba(56,189,248,0.3)]'
              : 'bg-[#132238]/60 text-gray-300 hover:bg-[#132238] hover:text-white border border-white/10'
          }`}
        >
          {t.allMedia}
        </button>
        <button
          onClick={() => setFilter('photo')}
          className={`px-4 py-2 rounded-2xl text-xs font-bold transition-all flex items-center gap-1.5 ${
            filter === 'photo'
              ? 'bg-[#132238] text-[#38BDF8] border-2 border-[#38BDF8] shadow-[0_0_15px_rgba(56,189,248,0.3)]'
              : 'bg-[#132238]/60 text-gray-300 hover:bg-[#132238] hover:text-white border border-white/10'
          }`}
        >
          <ImageIcon className="w-3.5 h-3.5" />
          <span>{t.photos}</span>
        </button>
        <button
          onClick={() => setFilter('video')}
          className={`px-4 py-2 rounded-2xl text-xs font-bold transition-all flex items-center gap-1.5 ${
            filter === 'video'
              ? 'bg-[#132238] text-[#38BDF8] border-2 border-[#38BDF8] shadow-[0_0_15px_rgba(56,189,248,0.3)]'
              : 'bg-[#132238]/60 text-gray-300 hover:bg-[#132238] hover:text-white border border-white/10'
          }`}
        >
          <Video className="w-3.5 h-3.5" />
          <span>{t.videos}</span>
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {filtered.map((item, idx) => (
          <div
            key={item.id}
            onClick={() => setActiveMediaIndex(idx)}
            className="bg-[#132238]/80 backdrop-blur-md rounded-3xl overflow-hidden border border-[#38BDF8]/20 group cursor-pointer hover:border-[#38BDF8] hover:shadow-[0_0_25px_rgba(56,189,248,0.3)] transition-all duration-300"
          >
            <div className="relative h-56 overflow-hidden">
              <img
                src={item.thumbnail || item.url}
                alt={getLocalized(item.title, currentLang)}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                {item.type === 'video' ? (
                  <div className="w-12 h-12 rounded-full bg-[#38BDF8] text-[#0B1426] flex items-center justify-center shadow-[0_0_20px_rgba(56,189,248,0.8)]">
                    <Play className="w-6 h-6 fill-current ml-0.5" />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#0B1426] text-[#38BDF8] flex items-center justify-center border border-[#38BDF8] shadow-[0_0_20px_rgba(56,189,248,0.5)]">
                    <Eye className="w-6 h-6" />
                  </div>
                )}
              </div>

              <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-[#0B1426]/90 text-[#38BDF8] text-[10px] font-bold border border-[#38BDF8]/40 shadow-md">
                {getLocalizedCategory(item.category, currentLang)}
              </span>
            </div>

            <div className="p-4">
              <h3 className="text-sm font-bold text-white group-hover:text-[#38BDF8] transition-colors truncate">
                {getLocalized(item.title, currentLang)}
              </h3>
              <p className="text-[10px] text-cyan-200/80 mt-1">{item.date}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox / Video Modal */}
      {activeMedia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-[#0B1426]/95 backdrop-blur-xl animate-in fade-in duration-200">
          <div className="bg-[#132238] w-full max-w-5xl rounded-[2.5rem] border-2 border-[#38BDF8] shadow-[0_0_50px_rgba(56,189,248,0.4)] overflow-hidden flex flex-col max-h-[92vh]">
            
            {/* Modal Navigation Header */}
            <div className="sticky top-0 z-30 bg-[#0B1426]/95 backdrop-blur-md px-4 sm:px-6 py-3 border-b border-[#38BDF8]/40 shadow-[0_4px_20px_rgba(56,189,248,0.25)] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrev}
                  className="px-3 py-1.5 rounded-full bg-[#132238] text-[#38BDF8] border border-[#38BDF8]/50 hover:bg-[#38BDF8] hover:text-[#0B1426] font-extrabold text-xs flex items-center gap-1 transition-all shadow-md"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Önceki</span>
                </button>
                <button
                  onClick={handleNext}
                  className="px-3 py-1.5 rounded-full bg-[#132238] text-[#38BDF8] border border-[#38BDF8]/50 hover:bg-[#38BDF8] hover:text-[#0B1426] font-extrabold text-xs flex items-center gap-1 transition-all shadow-md"
                >
                  <span className="hidden sm:inline">Sonraki</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <div className="text-center">
                <span className="px-3 py-0.5 rounded-full bg-[#38BDF8]/20 text-[#38BDF8] text-[10px] font-extrabold border border-[#38BDF8]/40 uppercase tracking-widest shadow-[0_0_10px_rgba(56,189,248,0.3)]">
                  {getLocalizedCategory(activeMedia.category, currentLang)}
                </span>
                <p className="text-[11px] text-gray-300 font-bold hidden sm:block truncate max-w-xs mt-0.5">
                  {getLocalized(activeMedia.title, currentLang)}
                </p>
              </div>

              <button
                onClick={() => setActiveMediaIndex(null)}
                className="px-3.5 py-1.5 rounded-full bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_15px_rgba(165,243,252,0.8)] transition-all flex items-center gap-1 shadow-md"
              >
                <span>Kapat</span>
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6 overflow-y-auto space-y-4">
              <div className="relative w-full rounded-[2rem] overflow-hidden border-2 border-[#38BDF8] shadow-[0_0_25px_rgba(56,189,248,0.5)] bg-black flex items-center justify-center max-h-[60vh]">
                {activeMedia.type === 'video' ? (
                  <iframe
                    src={activeMedia.url}
                    title="Video Player"
                    className="w-full h-72 sm:h-[450px] border-0"
                    allowFullScreen
                  />
                ) : (
                  <img
                    src={activeMedia.url}
                    alt={getLocalized(activeMedia.title, currentLang)}
                    className="max-w-full max-h-[60vh] object-contain rounded-2xl"
                  />
                )}
              </div>

              <div className="text-center space-y-1">
                <h3 className="text-lg font-bold text-white">
                  {getLocalized(activeMedia.title, currentLang)}
                </h3>
                <p className="text-xs text-cyan-200/80">{activeMedia.date}</p>
              </div>
            </div>

            {/* Modal Bottom Footer */}
            <div className="p-3 bg-[#0B1426] border-t border-[#38BDF8]/20 flex items-center justify-between text-xs">
              <div className="text-cyan-200/80 font-mono text-[11px]">
                Medya {activeMediaIndex !== null ? activeMediaIndex + 1 : 0} / {filtered.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => alert('Galeri medyası bağlantısı kopyalandı!')}
                  className="px-3 py-1.5 rounded-xl bg-[#132238] border border-[#38BDF8]/40 text-[#38BDF8] font-bold hover:bg-[#38BDF8] hover:text-[#0B1426] transition-all flex items-center gap-1.5"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  <span>Paylaş</span>
                </button>
                <button
                  onClick={() => setActiveMediaIndex(null)}
                  className="px-4 py-1.5 rounded-xl bg-[#38BDF8] text-[#0B1426] font-extrabold hover:bg-[#A5F3FC] transition-all"
                >
                  Tamam
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

